import { COMPETENCY_LABELS } from '@/lib/competency'
import { scoreBarColor, scoreColor } from '@/lib/scoreColors'
import { cn } from '@/lib/utils'
import type { Competency } from '@/types/interview'

interface CompetencyScore {
  competency: Competency
  averageScore: number
  sessionCount: number
}

interface CompetencyTrackerProps {
  scores: CompetencyScore[]
  className?: string
}

export function CompetencyTracker({ scores, className }: CompetencyTrackerProps) {
  if (scores.length === 0) {
    return (
      <div
        className={cn(
          'rounded-xl border border-border bg-muted/30 px-5 py-6 text-center',
          className
        )}
      >
        <p className="text-sm text-muted-foreground">
          Complete sessions to see your competency progress here.
        </p>
      </div>
    )
  }

  const sorted = [...scores].sort((a, b) => b.averageScore - a.averageScore)

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-background divide-y divide-border overflow-hidden',
        className
      )}
    >
      {sorted.map(({ competency, averageScore, sessionCount }) => {
        const pct = ((averageScore - 1) / 4) * 100
        return (
          <div key={competency} className="flex items-center gap-4 px-4 py-3">
            <div className="w-36 flex-shrink-0">
              <p className="text-sm font-medium">{COMPETENCY_LABELS[competency]}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {sessionCount} session{sessionCount === 1 ? '' : 's'}
              </p>
            </div>
            <div
              role="progressbar"
              tabIndex={-1}
              aria-valuenow={averageScore}
              aria-valuemin={1}
              aria-valuemax={5}
              aria-label={`${COMPETENCY_LABELS[competency]} average score`}
              className="flex-1 h-2 rounded-full bg-muted overflow-hidden"
            >
              <div
                className={cn('h-full rounded-full transition-all', scoreBarColor(averageScore))}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span
              className={cn(
                'w-8 text-right text-sm font-semibold tabular-nums',
                scoreColor(averageScore)
              )}
            >
              {averageScore.toFixed(1)}
            </span>
          </div>
        )
      })}
    </div>
  )
}
